import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Wordmark } from "@/components/landing/Wordmark";
import {
  appRedirectUrl,
  authRedirectUrl,
  resolveAuthView,
  translateAuthError,
  type AuthView,
} from "@/lib/authErrors";
import { UPGRADE_PATH, safeAppPath } from "@/lib/firstScene";
import { formatProMonthlyPrice } from "@/lib/pricing";
import { MIN_PASSWORD_LENGTH, PASSWORD_HINT, validatePassword } from "@/lib/passwordPolicy";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();

  const [view, setView] = useState<AuthView>(() =>
    resolveAuthView({
      search: window.location.search,
      hash: window.location.hash,
    }),
  );
  const recoveringRef = useRef(view === "update-password");
  const afterAuthPath = safeAppPath(searchParams.get("next")) ?? "/app";
  const headingToCheckout = afterAuthPath === UPGRADE_PATH;

  useEffect(() => {
    const next = resolveAuthView({
      search: window.location.search,
      hash: window.location.hash,
    });
    setView(next);
    recoveringRef.current = next === "update-password";
  }, [searchParams]);

  useEffect(() => {
    const checkUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const current = resolveAuthView({
        search: window.location.search,
        hash: window.location.hash,
      });
      if (session && current !== "update-password" && !recoveringRef.current) {
        navigate(afterAuthPath);
      }
    };
    void checkUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "PASSWORD_RECOVERY") {
        recoveringRef.current = true;
        setView("update-password");
        return;
      }
      if (event === "SIGNED_IN" && session) {
        const current = resolveAuthView({
          search: window.location.search,
          hash: window.location.hash,
        });
        if (recoveringRef.current || current === "update-password") {
          recoveringRef.current = true;
          setView("update-password");
          return;
        }
        navigate(afterAuthPath);
      }
    });

    return () => subscription.unsubscribe();
  }, [afterAuthPath, navigate]);

  const showError = (error: { message?: string; code?: string } | null, fallback: string) => {
    toast({
      title: "Ops, algo deu errado 😅",
      description: translateAuthError(error, fallback),
      variant: "destructive",
    });
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    const policy = validatePassword(password);
    if (!policy.ok) {
      toast({
        title: "Senha inválida",
        description: policy.message,
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: fullName,
        },
        emailRedirectTo: appRedirectUrl(undefined, afterAuthPath),
      },
    });

    if (error) {
      showError(error, "Não consegui criar a conta. Tente de novo.");
    } else {
      toast({
        title: "Cadastro feito! 🎉",
        description: "Se pedirem confirmação, olhe o e-mail. Depois pode entrar.",
      });
    }

    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      showError(error, "Não consegui entrar. Confira e-mail e senha.");
    }

    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: authRedirectUrl(window.location.origin, "update-password"),
    });

    if (error) {
      showError(error, "Não consegui enviar o e-mail de recuperação.");
    } else {
      toast({
        title: "E-mail enviado ✉️",
        description: "Se essa conta existir, você recebe um link para criar uma nova senha.",
      });
    }

    setLoading(false);
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const policy = validatePassword(password);
    if (!policy.ok) {
      toast({
        title: "Senha inválida",
        description: policy.message,
        variant: "destructive",
      });
      return;
    }
    if (password !== confirmPassword) {
      toast({
        title: "Senhas diferentes",
        description: "A confirmação precisa ser igual à nova senha.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      showError(error, "Não consegui salvar a nova senha. Peça outro link.");
    } else {
      toast({
        title: "Senha atualizada 🎉",
        description: "Pode continuar usando o app.",
      });
      navigate(afterAuthPath);
    }

    setLoading(false);
  };

  const inputClass = "h-[52px] rounded-md border-neutral-200 text-body text-neutral-900 placeholder:text-neutral-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md animate-fade-up">
        <div className="mb-8 text-center">
          <h1 className="sr-only">Talki</h1>
          <Wordmark className="justify-center" size="lg" />
          <p className="mt-3 text-muted-foreground">
            {headingToCheckout
              ? `Crie a conta para assinar o Talki Pro — ${formatProMonthlyPrice()}/mês.`
              : "Aeroporto, restaurante, reunião. O inglês que você usa de verdade."}
          </p>
        </div>

        <Card className="card-hello border-0 shadow-hello-lg">
          <CardContent className="p-6">
            {view === "forgot" && (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">Esqueci a senha</h2>
                  <p className="text-sm text-muted-foreground">
                    Digite o e-mail da conta. Se ele existir, mandamos um link para redefinir a senha.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={inputClass}
                    inputMode="email"
                    autoComplete="email"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} loading={loading}>
                  {loading ? "Enviando..." : "Enviar link"}
                </Button>
                <button
                  type="button"
                  className="w-full text-sm text-muted-foreground hover:text-foreground min-h-[44px]"
                  onClick={() => setView("signin")}
                >
                  Voltar para entrar
                </button>
              </form>
            )}

            {view === "update-password" && (
              <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-1">
                  <h2 className="text-lg font-semibold">Nova senha</h2>
                  <p className="text-sm text-muted-foreground">{PASSWORD_HINT}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nova senha</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder={PASSWORD_HINT}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    className={inputClass}
                    autoComplete="new-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirmar senha</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Repita a nova senha"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={MIN_PASSWORD_LENGTH}
                    className={inputClass}
                    autoComplete="new-password"
                  />
                </div>
                <Button type="submit" className="w-full" disabled={loading} loading={loading}>
                  {loading ? "Salvando..." : "Salvar senha"}
                </Button>
              </form>
            )}

            {(view === "signin" || view === "signup") && (
              <Tabs value={view} onValueChange={(value) => setView(value as AuthView)} className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted/50 p-1 rounded-xl">
                  <TabsTrigger
                    value="signin"
                    className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm min-h-[44px]"
                  >
                    Entrar
                  </TabsTrigger>
                  <TabsTrigger
                    value="signup"
                    className="rounded-lg data-[state=active]:bg-background data-[state=active]:shadow-sm min-h-[44px]"
                  >
                    Criar conta
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="signin" className="mt-0">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className={inputClass}
                        inputMode="email"
                        autoComplete="email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Senha</Label>
                      <Input
                        id="signin-password"
                        type="password"
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        className={inputClass}
                        autoComplete="current-password"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} loading={loading}>
                      {loading ? "Entrando..." : "Entrar"}
                    </Button>
                    <button
                      type="button"
                      className="w-full text-sm text-primary hover:underline min-h-[44px]"
                      onClick={() => setView("forgot")}
                    >
                      Esqueci a senha
                    </button>
                  </form>
                </TabsContent>

                <TabsContent value="signup" className="mt-0">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Como podemos te chamar?</Label>
                      <Input
                        id="signup-name"
                        type="text"
                        placeholder="Seu nome"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        className={inputClass}
                        autoComplete="name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="seu@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className={inputClass}
                        inputMode="email"
                        autoComplete="email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Crie uma senha</Label>
                      <Input
                        id="signup-password"
                        type="password"
                        placeholder={PASSWORD_HINT}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={MIN_PASSWORD_LENGTH}
                        className={inputClass}
                        autoComplete="new-password"
                      />
                      <p className="text-xs text-muted-foreground">{PASSWORD_HINT}</p>
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} loading={loading}>
                      {loading ? "Criando conta..." : headingToCheckout ? "Criar conta e continuar" : "Criar conta grátis"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Feito no Brasil
        </p>
      </div>
    </div>
  );
};

export default Auth;
